import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useTranslation } from "react-i18next";
import SaveAsDialog, { bindSaveAsDialog, useSaveAsDialogState } from "../../../components/save-as-dialog/SaveAsDialog";
import DataFileImportEditorUtils, { DataFileImportAdminMode, DataFileImportFormType, StepEnum, defaultImport } from "./DataFileImportEditorUtils";
import { useDataFileImportConfiguration } from "../../../queries/data-file-import/UseDataFileImportConfiguration";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useTimezones } from "../../../queries/time-zones/UseTimezones";
import { useForm, useWatch } from "react-hook-form";
import StepMenu, { StepDefinition } from "../../../components/step-menu/StepMenu";
import { useStepMenuState, StepMenuEditModeEnum } from "../../../components/step-menu/StepMenuState";
import { useDataFileImportConfigurations } from "../../../queries/data-file-import/UseDataFileImportConfigurations";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import Form from "../../../components/forms/form/Form";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import FileInformation from "./data-file-import-file-information/FileInformation";
import { Box } from "@mui/material";
import { IColumn, IObjectMapInfo, ITagTypeMapInfo, ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import Data from "./data-file-import-data/Data";
import Objects from "./data-file-import-objects/Objects";
import { IReferenceTimeZoneDTO } from "../../../services/global-data-service/Models";
import { useSaveDataFileImportConfiguration } from "../../../queries/data-file-import/UseSaveDataFileImportConfiguration";
import { useDataFileImportTagTypeMap } from "../../../queries/data-file-import/UseDataFileImportTagTypeMap";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import { useDataFileImportObjectMap } from "../../../queries/data-file-import/UseDataFileImportObjectMap";

export type DataFileImportEditorCloseType = {
    reason: "cancel" | "save";
    configurationID: number | undefined;
};

export type DataFileImportEditorProps = {
    //organizationID: number,
    groupID: number,
    configurationID: number;
    onClose: ({ reason }: DataFileImportEditorCloseType) => void;
};

export default function DataFileImportEditor({
    //organizationID,
    groupID,
    configurationID,
    onClose,
}: DataFileImportEditorProps): JSX.Element {

    const [updating, setUpdating] = useState(false);
    //const isPhone = isMobile && !isTablet;
    //const [userState] = useUserContext();
    const { confirm
        , acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
    const saveAsDialog = useSaveAsDialogState();

    const mode = (configurationID ?? 0) <= 0 ? 
                DataFileImportAdminMode.Add : 
                DataFileImportAdminMode.Edit;


    const form = useForm<DataFileImportFormType>({
        mode: 'onChange',
        defaultValues: {...defaultImport
        }
    });
            

    const {
        getValues,
        setValue,
        //watch,
        formState: {
            //dirtyFields,
            isDirty,
            isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;
    
    const importForm = useWatch({ control: control }) as DataFileImportFormType;

    const {data, status, refetch } = useQueryNotifications(useDataFileImportConfiguration)({
        groupID,
        configurationID,
    });

    const { data: timezones } = useQueryNotifications(useTimezones)();    

    // const {data: referenceData, status: referenceDataStatus } = useQueryNotifications(useReportNotifications)({
    //     groupID
    // });

    const {data: configurationList } = useQueryNotifications(useDataFileImportConfigurations)({
        groupID: groupID,
    });

    const objects = importForm.objectMap.map(o => {
        return {
            id: o.id,
            objectType: o.objectType

        } as IeLynxObject;
    });

    const tagTypeColumns = importForm.columns.filter(o => o.name !== importForm.dateColumn?.name &&
        o.name !== importForm.timeColumn?.name &&
        o.name !== importForm.identifierColumn?.name);


    const { data: importTagTypes } = useQueryNotifications(useDataFileImportTagTypeMap)({
        groupID,
        fileHasHeaderRow: importForm.fileHasHeaderRow,
        importObjectType: importForm.importObjectType,
        importType: importForm.importType,
        tagTypeColumns: tagTypeColumns,
        objects,
        enabled: tagTypeColumns.length > 0
    });


    const { data: importObjects } = useQueryNotifications(useDataFileImportObjectMap)({
        groupID,
        fileName: importForm.fileName,
        fileHasHeaderRow: importForm.fileHasHeaderRow,
        identifierColumn: importForm.identifierColumn as IColumn,
        importObjectType: importForm.importObjectType,
        enabled: (importForm.identifierColumn?.name.length ?? 0) > 0 && importForm.fileName !== undefined
    });



    const { mutate: saveImportConfiguration/*, status*/ } = useQueryNotifications(useSaveDataFileImportConfiguration)({
        groupID: groupID,

        onSuccess: async (data) => {
            acknowledge(t("dataFileImport.saveSuccessText", { 
                                name: data.name
                            }
                        ));

            await refetch();

            onClose({reason: "save", configurationID: data.id});
        },
        onError: () => {
            setUpdating(false);
        }
    });


    const isLoading = status === 'loading' ||
                    updating; 

    const fileInformationValid = importForm.fileName.length > 0 && importForm.dateColumn.name !== '';

    const objectsValid = (importForm.identifierColumn?.name.length ?? 0) > 0 && 
                                importForm.objectMap.length > 0 &&
                                importForm.objectMap.some(o => o.id === -1) === false;

    const dataValid = importForm.tagTypeMap.length > 0 && 
                                importForm.tagTypeMap.some(o => o.name.length === 0) === false;


    const hasChanges = isDirty && isValid && dataValid;

    const stepDefinitions: StepDefinition[] = useMemo(() => {

        const steps = [{
            key: StepEnum.FileInformation,
            title: t("dataFileImport.steps.fileInformation"),
            valid: fileInformationValid

        }, {
            key: StepEnum.Objects,
            title: t("dataFileImport.steps.objects"),
            disabled: !fileInformationValid,
            valid: objectsValid
        },{
            key: StepEnum.Data,
            title: t("dataFileImport.steps.data"),
            disabled: importForm.importType === ImportTypeEnum.Single ?
                                 !fileInformationValid :
                                 !objectsValid,
        }];

        return importForm.importType === ImportTypeEnum.Multiple ? 
                    steps :
                    steps.filter(o => o.key !== StepEnum.Objects);
    }, [
        t,
        fileInformationValid,
        objectsValid,
        importForm.importType
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveChangesButtonState,
            saveAsNewButtonState
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        setSaveAsNewButtonVisible,
        setSaveAsNewButtonLabel,
        setSaveAsNewButtonDisabled
        
    } = useStepMenuState(
        StepMenuEditModeEnum.Create,
        undefined,
        stepDefinitions
    );

    const updateButtonStates = useCallback(() => {

         switch (activeStep) {
            case StepEnum.FileInformation:
                setSaveChangesButtonLabel(t(mode === DataFileImportAdminMode.Add ? 
                        'buttons.saveAsNew' : 
                        'buttons.saveChanges'));
                setSaveAsNewButtonLabel(t('buttons.saveAsNew'));
                setNextButtonDisabled(!fileInformationValid);
                break;


            case StepEnum.Objects:
                
                setNextButtonDisabled(!objectsValid);
                break;


            case StepEnum.Data:
                //setSaveChangesButtonVisible(mode === DataFileImportAdminMode.Add);
                setSaveChangesButtonVisible(true);
                setSaveAsNewButtonVisible(mode === DataFileImportAdminMode.Edit);
                setSaveAsNewButtonDisabled(!isValid || !dataValid);
                setSaveChangesButtonDisabled(!hasChanges);
                break;

            default:
                break;
        }
    }, [
        t,
        mode,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        activeStep,
        fileInformationValid,
        objectsValid,
        dataValid,
        hasChanges,
        setSaveAsNewButtonLabel,
        setSaveAsNewButtonVisible,
        isValid,
        setSaveAsNewButtonDisabled
        
    ]);


    async function onCancelEdit() {

        if (!isDirty || (await getUserExitConfirmation())) {
            onClose({ 
                reason: "cancel", 
                configurationID: importForm.id
            });
        }
    }

    function onCancelStep() {
        onCancelEdit();
    }

    function onStepChange(fromStep: number, toStep: number) {

        setActiveStep(toStep);
    }

    async function onSaveChanges() {

        const values = getValues();

        const model = DataFileImportEditorUtils.mapToModel(values, 
                        importTagTypes as ITagTypeMapInfo, 
                        importObjects as IObjectMapInfo,
                        timezones as IReferenceTimeZoneDTO[]);

        saveImportConfiguration(model);

        
        // const model = DataFileImportEditorUtils.mapToModel(groupID, 
        //                             values, 
        //                             data as IEditReportTaskDTO, 
        //                             referenceData as IAvailableNotificationInfoDTO,
        //                             myReports);
        // saveReportTask(model);
    }


    function _validateName(value: string): boolean | string {

        return configurationList?.find(o => o.name.toLowerCase() === value.trim().toLowerCase()) === undefined ?
            true :
            t('dataFileImport.uniqueNameText') as string; 
            
    }

    async function onSaveAsNew() {
        const configuration = getValues();

        const result = await saveAsDialog.open({
            name: configuration.name,
            configurationType: t("configuration.types.dataFileImport"),
            validate: _validateName
        });

        if (result.reason === "ok") {
            setValue('name', result.values?.name as string )
            onSaveChanges();
        }
    }


    //function onExecuteAction(menuItem: ActionMenuItem) {

        // if (activeStep === StepEnum.Notifications) {
        //     const action = menuItem.action as RecipientActions;
        //     notificationsRef.current?.executeAction(action);
        // }
    //}

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    useEffect(() => {

        if (data && timezones) {

            reset(DataFileImportEditorUtils.mapToFormValues(data, timezones));
        }
    }, [
        data,
        timezones,
        reset,
        
    ]);


    useEffect(() => {

         updateButtonStates();
    }, [
        importForm, 
        updateButtonStates
    ]);

    return (
        <LoadingZone isLoading={isLoading}>
            {data && timezones && 
            <Form form={form} sx={{ padding: 0 }}>

                {/* {isPhone && 
                <MobileStepMenu 
                    steps={steps}
                    title={mode === DataFileImportAdminMode.Add ? 'New Scheduled Report': reportTaskFormData.name}
                    activeStep={activeStep}
                    onCancel={onCancelEdit}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    saveAsNewButtonState={saveAsNewButtonState}
                    onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                    onSaveChanges={handleSubmit(() => mode === DataFileImportAdminMode.Add ? onSaveAsNew() : onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                    viewOptionState={viewOptionState}
                    setViewOptionState={setViewOptionState}
                    onToggleOptionsAction={activeStep === StepEnum.Notifications ? () => setOpenSideSheet(!openSideSheet) : undefined}
                    actions={
                             activeStep === StepEnum.Notifications && notificationState.state.activeTab === DataFileImportNotificationTabEnum.Recipients ?
                                 recipientActionItems :
                            []  
                    }
                    onExecuteAction={onExecuteAction}
                >
                    {activeStep === StepEnum.Properties &&
                        <DataFileImportProperties 
                            groupID={groupID} 
                            myReports={myReports} />
                    }


                    {activeStep === StepEnum.Notifications &&

                        <DataFileImportNotifications 
                            organizationID={organizationID}
                            groupID={groupID} 
                            openSideSheet={openSideSheet} 
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            setViewOptionState={setViewOptionState} 
                            notificationState={notificationState}
                            ref={notificationsRef}

                        />
                    }

                    {activeStep === StepEnum.Summary &&
                        <DataFileImportSummary groupID={groupID}/>
                    }

                </MobileStepMenu>
                } */}


                <StepMenu
                    landing={landing}
                    steps={steps}
                    title={mode === DataFileImportAdminMode.Add ? 'New Import Configuration': importForm.name}
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    saveAsNewButtonState={saveAsNewButtonState}
                    onCancel={onCancelEdit}
                    onSaveAsNew={handleSubmit(() => onSaveAsNew())}
                    onSaveChanges={handleSubmit(() => mode === DataFileImportAdminMode.Add ? onSaveAsNew() : onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {/* {activeStep === StepEnum.FileInformation && */}
                    {/* the FileInformation component must be in the DOM to keep the uploaded file element present since it's state
                    cannot be restored during rerendering */}
                    <Box
                        sx={{
                            display: activeStep === StepEnum.FileInformation ? 'flex' : 'none',
                            flexDirection: 'column',
                            flex: '1 1 auto'
                        }}>
                        <FileInformation
                            groupID={groupID} 
                        />
                    </Box>                        
                    {/* } */}

                    {activeStep === StepEnum.Objects &&
                        <Objects groupID={groupID} />
                    }

                    {activeStep === StepEnum.Data &&
                        <Data groupID={groupID} />
                    }

                </StepMenu>
            </Form>
            }
            <SaveAsDialog {...bindSaveAsDialog(saveAsDialog)} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>
    );
}
